import React, { useState } from 'react';
import { situacaoService } from '../../services/entitiesServices';
import type { Situacao } from '../../types/entities';

interface SituacaoFormModalProps {
  situacao?: Situacao;
  onClose: () => void;
  onSuccess?: (situacao: Situacao) => void;
}

export const SituacaoFormModal: React.FC<SituacaoFormModalProps> = ({ situacao, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    nome: situacao?.nome || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let result;
      if (situacao) {
        // Editar
        result = await situacaoService.update(situacao.id, formData);
      } else {
        // Criar
        result = await situacaoService.create(formData);
      }

      onSuccess?.(result.data);
      onClose();
    } catch (err) {
      console.error('Erro ao salvar situação:', err);
      setError(err instanceof Error ? err.message : 'Erro ao salvar situação');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-gray-100 rounded-md flex items-center justify-center">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900">
                  {situacao ? 'Editar Situação' : 'Nova Situação'}
                </h3>
                {situacao?.nome && (
                  <p className="text-xs text-gray-500 mt-0.5">{situacao.nome}</p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-6 h-6 bg-gray-100 hover:bg-gray-200 rounded-md flex items-center justify-center text-gray-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 rounded-md bg-red-50 border border-red-200">
                <p className="text-xs text-red-700">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="nome" className="block text-xs font-medium text-gray-700 mb-1">
                Nome da Situação *
              </label>
              <input
                type="text"
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                placeholder="Digite o nome da situação"
                required
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Salvando...' : (situacao ? 'Atualizar' : 'Criar')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};